import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eye } from "lucide-react";
import Loader from "./loader";
import { Layout } from "../layout/Layout";
import { ProviderMainCard } from "../components/Profesional/ProviderMainCard";
import { ProviderDetails } from "../components/Profesional/ProviderDetails";
import { ProviderSideCard } from "../components/Profesional/ProviderSideCard";
import { getImageUri } from "../core/utils/imageLoad";
import {
	resolveProviderReviewsCount,
	resolveProviderStats,
} from "../core/utils/providerStats";
import { useAssignedBadges, useProvider } from "../hooks/control";

// ============================================================================
// TYPES
// ============================================================================
type ProviderServiceCategoryLink = {
	categories_providers?: {
		categories_services?: {
			name_category?: string;
			subcategories_services?: Array<{ name_subcategory?: string }>;
		};
	};
	subcategories_services_providers?: Array<{
		subcategories_services?: { name_subcategory?: string };
	}>;
};

type ProviderServiceRecord = {
	id: number;
	name_service: string;
	base_price: number | string | null;
	description_service?: string | null;
	is_active?: boolean | null;
	pictures_service?: string[] | null;
	categories_services_providers?:
		| ProviderServiceCategoryLink[]
		| ProviderServiceCategoryLink;
};

// ============================================================================
// HELPERS
// ============================================================================
const toMediaUrl = (raw?: string | null) => getImageUri(raw) ?? "";

const toProviderServiceRecord = (value: unknown): ProviderServiceRecord | null => {
	if (!value || typeof value !== "object") return null;
	const c = value as Record<string, unknown>;
	const id = Number(c.id);
	const name_service = String(c.name_service ?? "").trim();
	if (!Number.isFinite(id) || id <= 0 || !name_service) return null;
	return {
		id,
		name_service,
		base_price:
			typeof c.base_price === "number" || typeof c.base_price === "string"
				? c.base_price
				: null,
		description_service:
			typeof c.description_service === "string" ? c.description_service : null,
		is_active: typeof c.is_active === "boolean" ? c.is_active : null,
		pictures_service: Array.isArray(c.pictures_service)
			? (c.pictures_service as string[])
			: null,
		categories_services_providers:
			(c.categories_services_providers as
				| ProviderServiceCategoryLink[]
				| ProviderServiceCategoryLink
				| undefined) ?? undefined,
	};
};

const extractProviderServices = (payload: unknown): ProviderServiceRecord[] => {
	if (Array.isArray(payload)) {
		return payload
			.map(toProviderServiceRecord)
			.filter((e): e is ProviderServiceRecord => e !== null);
	}
	if (!payload || typeof payload !== "object") return [];
	const w = payload as { services_providers?: unknown[] };
	const col = Array.isArray(w.services_providers) ? w.services_providers : [];
	return col
		.map(toProviderServiceRecord)
		.filter((e): e is ProviderServiceRecord => e !== null);
};

const normalizeCategoryLabel = (value: string) =>
	value
		.toLowerCase()
		.normalize("NFD")
		.replace(/\p{Diacritic}/gu, "")
		.replace(/\s+/g, " ")
		.trim();

const dedupeCategoryLabels = (values: Array<string | null | undefined>) => {
	const seen = new Set<string>();
	const result: string[] = [];

	for (const value of values) {
		const label = String(value ?? "").trim();
		if (!label) continue;
		const key = normalizeCategoryLabel(label);
		if (!key || seen.has(key)) continue;
		seen.add(key);
		result.push(label);
	}

	return result;
};

// ============================================================================
// COMPONENT
// ============================================================================
export const ProviderPreview = () => {
	const navigate = useNavigate();
	const { id: urlId } = useParams<{ id: string }>();
	const providerId = urlId ? Number(urlId) : 0;

	const { data: providerData, loading: isProviderLoading } = useProvider(providerId);
	const { data: assignedBadgesData } = useAssignedBadges(providerId);

	const [galleryImages, setGalleryImages] = useState<string[]>([]);

	// ── Computed services ──────────────────────────────────────────────────
	const providerServices = useMemo<ProviderServiceRecord[]>(() => {
		const all = extractProviderServices(providerData?.services_providers);
		const active = all.filter((s) => s.is_active !== false);
		return active.length > 0 ? active : all;
	}, [providerData?.services_providers]);

	const fullServices = useMemo(() => {
		return providerServices.map((srv) => ({
			id: srv.id,
			name_service: srv.name_service,
			base_price: Number.isFinite(Number(srv.base_price))
				? Number(srv.base_price)
				: null,
			description_service: srv.description_service ?? null,
		}));
	}, [providerServices]);

	const cheapestPrice = useMemo(() => {
		const values = providerServices
			.map((s) => Number(s.base_price))
			.filter((p) => Number.isFinite(p) && p > 0);
		return values.length > 0 ? Math.min(...values) : undefined;
	}, [providerServices]);

	// ── Gallery ────────────────────────────────────────────────────────────
	useEffect(() => {
		const serviceImages = providerServices
			.flatMap((s) => (Array.isArray(s.pictures_service) ? s.pictures_service : []))
			.map(toMediaUrl)
			.filter(Boolean);

		const portfolioImages = (providerData?.portfolio_providers ?? [])
			.flatMap((p) =>
				Array.isArray(p.pictures_portfolio) ? p.pictures_portfolio : [],
			)
			.map(toMediaUrl)
			.filter(Boolean);

		setGalleryImages(Array.from(new Set([...serviceImages, ...portfolioImages])));
	}, [providerData?.portfolio_providers, providerServices]);

	// ── Display info ───────────────────────────────────────────────────────
	const providerDisplayInfo = useMemo(() => {
		if (!providerData) return undefined;

		const addresses = Array.isArray(providerData.users?.addresses)
			? providerData.users.addresses
			: [];

		const firstAddr = addresses.find((a) => {
			const city = a.name_city?.trim();
			const state = a.name_state?.trim();
			const street = a.street_address?.trim();
			const label = a.name_address?.trim();
			return Boolean(city || state || street || label);
		});

		const locationStr = (() => {
			if (!firstAddr) return "Ubicación no especificada";
			const city = firstAddr.name_city?.trim() || "";
			const state = firstAddr.name_state?.trim() || "";
			const parts = [city, state].filter((s) => s.length > 0);
			if (parts.length > 0) return parts.join(", ");
			return (
				firstAddr.street_address?.trim() ||
				firstAddr.name_address?.trim() ||
				"Ubicación no especificada"
			);
		})();

		const displayName =
			providerData.company_name ||
			`${providerData.users?.names ?? ""} ${providerData.users?.last_names ?? ""}`.trim();

		const badgesSource =
			assignedBadgesData && assignedBadgesData.length > 0
				? assignedBadgesData.map((a) => a.badges)
				: (providerData.badges_providers || []).map((a) => a.badges);

		const badges = badgesSource.filter(Boolean).map((b) => ({
			id: b.id,
			name_badge: b.name_badge,
			description_badge: b.description_badge,
			icon_badge: toMediaUrl(b.icon_badge),
			type_badge: b.type_badge,
		}));

		// Extract categories as array or string, matching ProviderInfo
		const providerCategories = Array.from(
			new Map(
				(providerData.categories_providers ?? [])
					.map((assignment) => assignment?.categories_services)
					.filter(
						(category): category is NonNullable<typeof category> =>
							typeof category?.name_category === "string" &&
							category.name_category.trim().length > 0,
					)
					.map((category) => [
						category.id,
						{
							name: category.name_category.trim(),
							icon:
								toMediaUrl(
									category.icon_category ??
										category.picture_category ??
										null,
								) || null,
						},
					]),
			).values(),
		);
		const firstServiceLinks = Array.isArray(
			providerServices[0]?.categories_services_providers,
		)
			? providerServices[0]?.categories_services_providers
			: providerServices[0]?.categories_services_providers
				? [providerServices[0].categories_services_providers]
				: [];
		const fallbackServiceCategory =
			firstServiceLinks[0]?.categories_providers?.categories_services;

		let categoryName: string | string[] = "General";
		if (
			Array.isArray(providerData.categories_providers) &&
			providerData.categories_providers.length > 0
		) {
			const allCategories = dedupeCategoryLabels(
				providerData.categories_providers.map((assignment) =>
					assignment?.categories_services?.name_category?.trim(),
				),
			);
			if (allCategories.length > 1) {
				categoryName = allCategories;
			} else if (allCategories.length === 1) {
				categoryName = allCategories[0];
			}
		} else if (fallbackServiceCategory?.name_category?.trim()) {
			categoryName = fallbackServiceCategory.name_category.trim();
		}
		const categoryIcon = providerCategories[0]?.icon || undefined;

		const reviewsCount = resolveProviderReviewsCount(providerData);
		const stats = resolveProviderStats(providerData);

		// Extract avatarUrl as in ProviderInfo
		const avatarRaw = providerData.users?.image_profile;
		const avatarUrl = avatarRaw ? toMediaUrl(avatarRaw) : undefined;

		return {
			id: providerData.id,
			name: displayName,
			description:
				providerData.description_provider || "Sin descripción disponible.",
			rating: providerData.rating,
			reviews: reviewsCount,
			location: locationStr,
			languages: providerData.languages_provider,
			categories: providerCategories,
			category: categoryName,
			categoryIcon,
			experienceYears: providerData.experience_years ?? 0,
			reviewsList: providerData.users?.reviews_qualified || [],
			badges,
			faq: providerData.FAQ || [],
			stats,
			avatarUrl,
		};
	}, [assignedBadgesData, providerData, providerServices]);

	// ============================================================================
	// RENDER
	// ============================================================================
	return (
		<Layout>
			{/* Banner de vista previa */}
			<div className='sticky top-0 z-20 flex items-center gap-3 border-b border-primary/20 bg-primary/10 px-4 py-3 backdrop-blur-sm'>
				<Eye className='h-4 w-4 shrink-0 text-primary' />
				<p className='flex-1 text-sm font-medium text-primary'>
					Vista previa — así te ven los clientes
				</p>
				<button
					type='button'
					onClick={() => navigate("/service/profile")}
					className='cursor-pointer inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-primary-hover'
				>
					<ArrowLeft className='h-3.5 w-3.5' />
					Regresar
				</button>
			</div>

			<div className='min-w-0 bg-slate-50 px-4 py-6 md:px-6 lg:px-8'>
				<div className='mx-auto w-full max-w-7xl'>
					{isProviderLoading && !providerData ? (
						<div className='flex justify-center items-center h-64 text-slate-500'>
							<Loader />
						</div>
					) : !providerData ? (
						<div className='flex justify-center items-center h-64 text-slate-500'>
							<div className='text-lg font-semibold'>
								Proveedor no encontrado o no disponible.
							</div>
						</div>
					) : (
						<div className='grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_380px]'>
							<div className='min-w-0 lg:col-start-1 lg:row-start-1'>
								<ProviderMainCard
									name={providerDisplayInfo?.name}
									description={providerDisplayInfo?.description}
									rating={providerDisplayInfo?.rating}
									reviews={providerDisplayInfo?.reviews}
									location={providerDisplayInfo?.location}
									stats={providerDisplayInfo?.stats}
									experience={providerDisplayInfo?.experienceYears}
									isFavorite={false}
									favoriteLoading={false}
									onToggleFavorite={() => {}}
									avatarUrl={providerDisplayInfo?.avatarUrl}
								/>
							</div>

							<div className='lg:col-start-2 lg:row-span-2 lg:self-start'>
								<ProviderSideCard
									location={providerDisplayInfo?.location}
									responseTime={
										providerDisplayInfo?.stats?.responseTime
									}
									priceFrom={
										cheapestPrice
											? `$${cheapestPrice.toLocaleString("es-VE", {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}`
											: "Consultar"
									}
									category={providerDisplayInfo?.category}
									categoryIcon={providerDisplayInfo?.categoryIcon}
									categories={providerDisplayInfo?.categories}
									onHire={() => {}}
									schedules={providerData?.schedules_providers}
								/>
							</div>

							<div className='min-w-0 lg:col-start-1 lg:row-start-2'>
								<ProviderDetails
									name={providerDisplayInfo?.name}
									description={providerDisplayInfo?.description}
									category={providerDisplayInfo?.category}
									categoryIcon={providerDisplayInfo?.categoryIcon}
									categories={providerDisplayInfo?.categories}
									rating={providerDisplayInfo?.rating}
									reviews={providerDisplayInfo?.reviews}
									experience={providerDisplayInfo?.experienceYears}
									faq={providerDisplayInfo?.faq}
									badges={providerDisplayInfo?.badges}
									languages={providerDisplayInfo?.languages}
									reviewsList={providerDisplayInfo?.reviewsList || []}
									services={fullServices}
									galleryImages={galleryImages}
								/>
							</div>
						</div>
					)}
				</div>
			</div>
		</Layout>
	);
};
